import Phaser from 'phaser';
import { SceneKeys } from '../game/Constants';
import { GameState } from '../game/GameState';
import { DataRegistry } from '../systems/DataRegistry';
import { EconomyManager } from '../systems/EconomyManager';
import { ProvinceManager } from '../systems/ProvinceManager';
import { RecruitmentManager } from '../systems/RecruitmentManager';
import { ReligionCultureManager } from '../systems/ReligionCultureManager';
import { SupplyManager } from '../systems/SupplyManager';
import type { PopulationGroupId } from '../types/game';
import { VisualTheme } from '../ui/VisualTheme';
import { createTextButton } from '../ui/components/UIButton';
import { addBackButton, addMetric, drawPanel, drawScreenBackground } from '../ui/components/SceneChrome';

const GROUP_LABELS: Record<PopulationGroupId, string> = {
  peasants: 'Peasants',
  artisans: 'Artisans',
  merchants: 'Merchants',
  nobles: 'Nobles',
  clergy: 'Clergy',
  soldiers: 'Soldiers',
};

export class ProvinceScene extends Phaser.Scene {
  constructor() {
    super(SceneKeys.Province);
  }

  create() {
    this.render();
  }

  private render() {
    this.children.removeAll();
    const campaign = GameState.instance.requireCampaign();
    const registry = DataRegistry.instance;
    const provinceId = campaign.selectedProvinceId ?? Object.keys(campaign.provinces)[0];
    const province = registry.provinces[provinceId];
    const state = campaign.provinces[provinceId];
    if (!province || !state) return;
    const owner = registry.factions[state.owner];
    const ledger = state.lastEconomy;

    drawScreenBackground(this, province.name, `${owner?.name ?? state.owner}  •  living province economy and administration`);
    addBackButton(this);

    drawPanel(this, 220, 220, 360, 220, 'Province profile');
    addMetric(this, 62, 158, 'Population', this.compact(state.population));
    addMetric(this, 62, 218, 'Workforce', `${state.workforce}`);
    addMetric(this, 62, 278, 'Food security', `${Math.round(state.foodSecurity)}%`, state.foodSecurity < 40 ? VisualTheme.text.danger : VisualTheme.text.success);
    addMetric(this, 225, 158, 'Prosperity', Math.round(state.prosperity));
    addMetric(this, 225, 218, 'Unrest', Math.round(state.unrest), state.unrest > 20 ? VisualTheme.text.danger : VisualTheme.text.body);
    addMetric(this, 225, 278, 'Supply', SupplyManager.instance.provinceSupplyCapacity(provinceId));

    drawPanel(this, 650, 220, 460, 220, 'Population and workforce');
    const totalPopulation = Math.max(1, state.population);
    (Object.keys(GROUP_LABELS) as PopulationGroupId[]).forEach((group, index) => {
      const x = 450 + (index % 2) * 220;
      const y = 154 + Math.floor(index / 2) * 54;
      const value = state.populationGroups[group] ?? 0;
      const share = value / totalPopulation;
      this.add.text(x, y, `${GROUP_LABELS[group]}  ${this.compact(value)}`, {
        fontFamily: VisualTheme.fonts.body,
        fontSize: '14px',
        color: VisualTheme.text.body,
      });
      this.add.rectangle(x + 88, y + 28, 190, 10, VisualTheme.colors.slate, 1).setOrigin(0.5).setStrokeStyle(1, VisualTheme.colors.gold, 0.3);
      this.add.rectangle(x - 7, y + 28, Math.max(2, 190 * share), 8, this.populationColor(group), 0.95).setOrigin(0, 0.5);
    });

    drawPanel(this, 1240, 220, 650, 220, 'Local market');
    const marketGoods = registry.goodList()
      .map((good) => ({ good, price: state.marketPrices[good.id] ?? good.basePrice, stock: state.storage[good.id] ?? 0 }))
      .sort((left, right) => Math.abs(right.price / right.good.basePrice - 1) - Math.abs(left.price / left.good.basePrice - 1))
      .slice(0, 10);
    marketGoods.forEach((entry, index) => {
      const column = index % 2;
      const row = Math.floor(index / 2);
      const x = 940 + column * 300;
      const y = 150 + row * 32;
      const ratio = entry.price / Math.max(0.01, entry.good.basePrice);
      const color = ratio > 1.35 ? VisualTheme.text.danger : ratio < 0.78 ? VisualTheme.text.success : VisualTheme.text.body;
      this.add.text(x, y, `${entry.good.name.padEnd(13)} ${entry.stock.toFixed(1).padStart(6)}   ${entry.price.toFixed(2)}g`, {
        fontFamily: 'Consolas, monospace',
        fontSize: '14px',
        color,
      });
    });
    this.add.text(940, 310, `Storage ${this.stockTotal(state.storage).toFixed(0)} / ${EconomyManager.instance.storageCapacity(provinceId)}  •  Roads ${state.roadLevel ?? 0}  •  Port ${province.port ? state.portLevel ?? 1 : 0}`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '13px',
      color: VisualTheme.text.muted,
    });

    drawPanel(this, 400, 610, 720, 500, 'Production, buildings and construction');
    const queue = state.constructionQueue.map((item) => `${registry.buildings[item.buildingId]?.name ?? item.buildingId} L${item.targetLevel ?? 1} (${item.remainingMonths}m)`).join('  •  ');
    this.add.text(68, 392, `Queue: ${queue || 'No construction underway'}`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '14px',
      color: queue ? VisualTheme.text.accent : VisualTheme.text.muted,
      wordWrap: { width: 660 },
    });

    const existing = state.buildings
      .map((id) => registry.buildings[id])
      .filter((building): building is NonNullable<typeof building> => Boolean(building))
      .sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name));
    existing.slice(0, 8).forEach((building, index) => {
      const column = index % 2;
      const row = Math.floor(index / 2);
      const x = 74 + column * 342;
      const y = 438 + row * 80;
      const level = state.buildingLevels[building.id] ?? 1;
      const max = building.maxLevel ?? 1;
      const expected = Object.entries(building.outputs ?? {}).map(([good, amount]) => `${registry.goods[good]?.name ?? good} +${amount * level}`).join(', ') || building.category;
      this.add.rectangle(x + 158, y + 27, 320, 68, VisualTheme.colors.slate, 0.78).setStrokeStyle(1, VisualTheme.colors.gold, 0.35);
      this.add.text(x + 10, y, `${building.name}  L${level}/${max}`, {
        fontFamily: VisualTheme.fonts.display,
        fontSize: '16px',
        color: VisualTheme.text.title,
      });
      this.add.text(x + 10, y + 24, `${expected}
Labor ${building.workforce ?? 0}  •  upkeep ${building.maintenanceGold ?? 0}g`, {
        fontFamily: VisualTheme.fonts.body,
        fontSize: '12px',
        color: VisualTheme.text.muted,
      });
      if (level < max) {
        const cost = ProvinceManager.instance.upgradeCost(building.id, level + 1);
        createTextButton(this, {
          label: `Upgrade ${cost}g`,
          x: x + 244,
          y: y + 29,
          width: 135,
          height: 34,
          fontSize: 11,
          onClick: () => {
            ProvinceManager.instance.upgradeBuilding(provinceId, building.id);
            this.render();
          },
        });
      }
    });

    const buildable = registry.buildingList()
      .filter((building) => !state.buildings.includes(building.id) && !state.constructionQueue.some((item) => item.buildingId === building.id))
      .filter((building) => !building.requiresPort || province.port)
      .slice(0, 4);
    buildable.forEach((building, index) => {
      createTextButton(this, {
        label: `Build ${building.name} (${building.costGold}g)`,
        x: 175 + (index % 2) * 335,
        y: 800 + Math.floor(index / 2) * 45,
        width: 315,
        height: 36,
        fontSize: 12,
        onClick: () => {
          ProvinceManager.instance.constructBuilding(provinceId, building.id);
          this.render();
        },
      });
    });

    drawPanel(this, 1190, 610, 820, 500, 'Monthly ledger and governance');
    const summary = ledger
      ? [
          `Tax collected: ${ledger.taxIncome.toFixed(1)}g`,
          `Maintenance: ${ledger.maintenance.toFixed(1)}g`,
          `Corruption loss: ${ledger.corruptionLoss.toFixed(1)}g`,
          `Transport: ${ledger.transportCost.toFixed(1)}g`,
          `Workforce: ${ledger.workforceUsed}/${ledger.workforceCapacity}`,
          `Food: ${ledger.foodConsumed.toFixed(1)}/${ledger.foodNeed.toFixed(1)}`,
          `Population: ${ledger.populationChange >= 0 ? '+' : ''}${ledger.populationChange.toFixed(2)}`,
          `Imports: ${this.formatGoods(ledger.imports) || 'none'}`,
          `Exports: ${this.formatGoods(ledger.exports) || 'none'}`,
        ]
      : ['No monthly ledger yet. End the month to run the first economy cycle.'];
    this.add.text(820, 392, summary.join('\n'), {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '14px',
      color: VisualTheme.text.body,
      lineSpacing: 5,
      wordWrap: { width: 345 },
    });
    this.add.text(1180, 392, `Tax rate ${Math.round(state.taxRate * 100)}%\nAutonomy ${Math.round(state.autonomy)}%\nCorruption ${Math.round(state.corruption)}%\nLoyalty ${Math.round(state.loyalty)}%\nDevastation ${Math.round(state.devastation)}%`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '15px',
      color: VisualTheme.text.accent,
      lineSpacing: 8,
    });
    if (ledger?.notes.length) {
      this.add.text(1180, 545, ledger.notes.slice(0, 4).map((note) => `• ${note}`).join('\n'), {
        fontFamily: VisualTheme.fonts.body,
        fontSize: '13px',
        color: VisualTheme.text.danger,
        lineSpacing: 5,
        wordWrap: { width: 360 },
      });
    }

    const governance = [
      ['Low tax 70%', () => ProvinceManager.instance.setTaxRate(provinceId, 0.7)],
      ['Normal tax 100%', () => ProvinceManager.instance.setTaxRate(provinceId, 1)],
      ['High tax 130%', () => ProvinceManager.instance.setTaxRate(provinceId, 1.3)],
      ['Grant autonomy', () => ProvinceManager.instance.encourageAutonomy(provinceId)],
      ['Centralize', () => ProvinceManager.instance.centralizeAdministration(provinceId)],
      ['Audit officials', () => ProvinceManager.instance.auditOfficials(provinceId)],
      ['Improve roads', () => ProvinceManager.instance.improveRoads(provinceId)],
      ['Supply depot', () => ProvinceManager.instance.setSupplyDepot(provinceId)],
      ['Expand port', () => ProvinceManager.instance.expandPort(provinceId)],
      ['Suppress unrest', () => ProvinceManager.instance.suppressUnrest(provinceId)],
      ['Convert religion', () => ReligionCultureManager.instance.convertReligion(provinceId)],
      ['Integrate culture', () => ReligionCultureManager.instance.integrateCulture(provinceId)],
    ] as const;
    governance.forEach(([label, action], index) => {
      createTextButton(this, {
        label,
        x: 835 + (index % 4) * 190,
        y: 690 + Math.floor(index / 4) * 48,
        width: 176,
        height: 36,
        fontSize: 12,
        onClick: () => {
          action();
          this.render();
        },
      });
    });

    const roster = owner?.roster ?? [];
    roster.slice(0, 3).forEach((unitId, index) => {
      const unit = registry.units[unitId];
      if (!unit) return;
      createTextButton(this, {
        label: `Recruit ${unit.name} (${unit.costGold}g)`,
        x: 930 + index * 220,
        y: 850,
        width: 205,
        height: 36,
        fontSize: 11,
        onClick: () => {
          RecruitmentManager.instance.recruit(state.owner, provinceId, unit.id);
          this.render();
        },
      });
    });

    const exposed = window as unknown as { render_game_to_text?: () => string; advanceTime?: (ms: number) => void };
    exposed.render_game_to_text = () => GameState.instance.renderToText();
    exposed.advanceTime = () => undefined;
  }

  private populationColor(group: PopulationGroupId) {
    return group === 'peasants' ? 0x6f9b54
      : group === 'artisans' ? 0xb77b46
        : group === 'merchants' ? 0x3f8ca1
          : group === 'nobles' ? 0x9b5daa
            : group === 'clergy' ? 0xd1b563
              : 0x9f4a48;
  }

  private stockTotal(storage: Record<string, number>) {
    return Object.values(storage).reduce((sum, value) => sum + Math.max(0, value), 0);
  }

  private formatGoods(goods: Record<string, number>) {
    return Object.entries(goods)
      .filter(([, amount]) => amount > 0)
      .slice(0, 4)
      .map(([id, amount]) => `${DataRegistry.instance.goods[id]?.name ?? id} ${amount.toFixed(1)}`)
      .join(', ');
  }

  private compact(value: number) {
    if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
    return value.toFixed(value < 100 ? 1 : 0);
  }
}
